import { isLeft } from "fp-ts/lib/Either";
import * as t from "io-ts";
const optional = (type) => t.union([type, t.undefined]);
export const FieldValue = t.union([t.string, t.number, t.boolean]);
const FieldWithValue = t.type({
    name: t.string,
    value: FieldValue
});
export const PrimitiveType = t.union([
    t.literal("string"),
    t.literal("number"),
    t.literal("boolean")
]);
export const FieldWithType = t.type({
    name: t.string,
    type: PrimitiveType
});
export const FieldWithTypeAndValue = t.intersection([
    FieldWithValue,
    FieldWithType
]);
export const SuggestedField = t.intersection([
    FieldWithType,
    t.partial({ id: t.string, defaultValue: FieldValue })
]);
export const WithID = t.type({
    id: t.string
});
export const FieldWithTypeAndID = t.intersection([FieldWithType, WithID]);
export const FieldWithTypeValueID = t.intersection([
    FieldWithTypeAndValue,
    WithID
]);
const Action = t.type({
    title: t.string
});
export const Flags = t.partial({
    ["show-immediately"]: t.boolean
});
export const ActionWithID = t.intersection([Action, WithID]);
const ActionWithTypeAndID = t.intersection([
    ActionWithID,
    t.partial({
        type: t.string // this will be tightly coupled to glide's library of actions
    })
]);
// export type ActionWithTypeAndID = t.TypeOf<typeof ActionWithTypeAndID>;
export const SuggestedAction = ActionWithTypeAndID;
const galleryRecord = {
    iterationResults: t.number,
    inspirationResults: t.number, // otherwise use iterationResults
    minimumSimilarity: t.number // 0-1
};
const RequiredGallery = t.type(galleryRecord);
export const Gallery = t.partial(galleryRecord);
export const BuilderPayloadNext = t.intersection([
    t.type({
        previous: optional(t.string),
        environment: t.partial({
            fields: t.array(FieldWithTypeAndID),
            actions: t.array(ActionWithID)
        }),
        message: optional(t.string),
        fields: t.array(FieldWithType),
        actions: t.array(Action)
    }),
    t.partial({
        message: t.string,
        generator: t.string,
        bodyClassName: t.string,
        instructions: t.string,
        useCache: t.boolean,
        gallery: Gallery,
        source: t.string,
        flags: Flags
    })
]);
export const BuilderPayload = t.intersection([
    t.type({
        prompt: t.string,
        fields: t.array(t.intersection([FieldWithValue, FieldWithType]))
    }),
    t.partial({
        generator: t.string,
        actions: t.array(Action),
        bodyClassName: t.string,
        instructions: t.string
    })
]);
export const GeneratedComponentNext = t.type({
    generated: t.string,
    html: t.string,
    fields: t.array(SuggestedField),
    actions: t.array(SuggestedAction)
});
const PublishedDetails = t.intersection([
    t.type({
        fields: t.array(SuggestedField),
        actions: t.array(SuggestedAction)
    }),
    t.partial({
        componentName: t.string
    })
]);
export const CommitInfo = t.intersection([
    t.type({
        id: t.string,
        fields: t.array(SuggestedField),
        previous: t.union([t.string, t.null, t.undefined])
    }),
    t.partial({
        generator: t.string,
        message: t.string,
        source: t.string,
        suggestedActions: t.array(SuggestedAction),
        flags: Flags,
        published: PublishedDetails,
        componentName: t.string
    })
]);
export const Commit = t.intersection([
    CommitInfo,
    t.type({
        generated: t.string,
        html: t.string
    })
]);
export const decodeGeneratedComponent = (value) => {
    const result = Commit.decode(value);
    if (isLeft(result)) {
        throw new Error(`Invalid generated component: ${JSON.stringify(result.left)}`);
    }
    return result.right;
};
const Column = t.type({
    name: t.string,
    type: PrimitiveType
});
export const Table = t.type({
    name: t.string,
    columns: t.array(Column)
});
export const TableWithRows = t.intersection([
    Table,
    t.type({
        rows: t.array(t.record(t.string, t.union([t.string, t.number, t.boolean])))
    })
]);
const SuccessResponse = (data) => t.type({
    ok: t.literal(true),
    data
});
const ErrorRepsonse = t.type({
    ok: t.literal(false),
    error: t.string
});
const Response = (data) => ({
    Data: data,
    Response: t.union([SuccessResponse(data), ErrorRepsonse])
});
export const CommitHistory = t.type({
    commit: Commit,
    history: t.array(CommitInfo)
});
export const CommitInfoResponse = Response(CommitHistory);
export const decodeCommitHistory = (value) => {
    const result = CommitHistory.decode(value);
    if (isLeft(result)) {
        throw new Error(`Invalid commit history: ${JSON.stringify(result.left)}`);
    }
    return result.right;
};
const GenerateTableResponse = Response(TableWithRows);
export const View2Response = t.intersection([
    t.type({
        html: t.string
    }),
    t.partial({
        flags: Flags
    })
]);
export const PublishPayload = t.intersection([
    t.type({
        id: t.string
    }),
    t.partial({
        published: PublishedDetails
    })
]);
